import { useState, useCallback } from "react";
import { useGetList, useRecordContext } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { FileText, Plus, DollarSign } from "lucide-react";
import { EstimateStatusBadge } from "./EstimateStatusBadge";
import { EstimateDetail } from "./EstimateDetail";
import { EstimateCreateDialog } from "./EstimateCreateDialog";
import type { BldrOsContact } from "../types";

interface Estimate {
  id: string;
  estimate_number: string;
  status: string;
  title: string;
  total: number;
  created_at: string;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

interface ContactEstimateListProps {
  contactId?: string | number;
}

/**
 * Estimate list for a specific contact.
 * Renders inside the ContactShowTabs "Estimates" tab.
 * Supports drill-down into EstimateDetail and create dialog.
 */
export function ContactEstimateList({ contactId }: ContactEstimateListProps) {
  const record = useRecordContext<BldrOsContact>();
  const resolvedId = contactId ?? record?.id;
  const contactName = record
    ? `${record.first_name || ""} ${record.last_name || ""}`.trim()
    : "";

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isPending, total } = useGetList<Estimate>("estimates", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "created_at", order: "DESC" },
    filter: resolvedId ? { contact_id: resolvedId } : {},
  });

  const handleCreated = useCallback((newId: string) => {
    setSelectedId(newId);
  }, []);

  if (!resolvedId) {
    return (
      <div className="text-sm text-muted-foreground py-4">
        No contact selected.
      </div>
    );
  }

  // Drill-down into detail
  if (selectedId) {
    return (
      <EstimateDetail
        estimateId={selectedId}
        onBack={() => setSelectedId(null)}
      />
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold flex items-center gap-1.5">
          <FileText className="w-4 h-4" />
          Estimates
          {total !== undefined && total > 0 && (
            <span className="text-xs text-muted-foreground font-normal">
              ({total})
            </span>
          )}
        </h4>
        <button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors cursor-pointer"
        >
          <Plus className="w-3 h-3" />
          New Estimate
        </button>
      </div>

      {/* Loading */}
      {isPending && (
        <div className="text-sm text-muted-foreground py-4">
          Loading estimates…
        </div>
      )}

      {/* Empty */}
      {!isPending && (!data || data.length === 0) && (
        <Card>
          <CardContent className="p-6 text-center">
            <FileText className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-40" />
            <p className="text-sm text-muted-foreground mb-3">
              No estimates yet for this contact.
            </p>
            <button
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Create First Estimate
            </button>
          </CardContent>
        </Card>
      )}

      {/* List */}
      {data && data.length > 0 && (
        <div className="space-y-2">
          {data.map((est) => (
            <Card
              key={est.id}
              className="cursor-pointer hover:border-blue-200 transition-colors"
              onClick={() => setSelectedId(est.id)}
            >
              <CardContent className="p-3">
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-mono text-muted-foreground">
                        {est.estimate_number}
                      </span>
                      <EstimateStatusBadge status={est.status} />
                    </div>
                    <p className="text-sm font-medium truncate">
                      {est.title || "Untitled"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(est.created_at)}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold tabular-nums flex items-center gap-1">
                      <DollarSign className="w-3.5 h-3.5 text-muted-foreground" />
                      {formatCurrency(Number(est.total))}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <EstimateCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        contactId={resolvedId}
        contactName={contactName}
        onCreated={handleCreated}
      />
    </div>
  );
}
